import { Instance, resolveIdentifier } from 'mobx-state-tree';
import { IStopFormTimeWindow } from '../../../components/orders/OrderForm/orderFormHelpers';
import { TCustomField } from '../../../types/TCustomField';
import { VisitDtoModelBase } from './VisitDtoModel.base';
import getAppRoot from '../utils/getAppRoot';
import { RouteDtoModel, RouteDtoModelType } from './RouteDtoModel';
import { MapMarkerSnapshot } from './MapMarkerModel';
import { VisitTimeWindowDtoModelType } from './VisitTimeWindowDtoModel';
import { VisitLocationDtoModelType } from './VisitLocationDtoModel';
import parseCustomFields from '../../../helpers/visits/parseCustomFields';
import { LocationStatus } from './LocationStatusEnum';
import { IColor } from '../../../types/IColor';
import { DeliveryMethod, OrderCompletionStatus, OrderEventLogDtoModelType, RouteSolutionDtoModelType, RouteTimelineDtoModelType } from '.';
import { Theme } from '@routific/hive';
import getScaledColor from '../../../helpers/getScaledColor';
import { UNSCHEDULED_COLOR_HEX } from '../../../constants';
import { isLandline } from '../../../helpers/forms/phoneUtils';
import { OrderSource } from '../../../types/TOrderSource';

// Unscheduled marker color — scaled once from a static hex (no theme dependency), so it's safe at
// module scope. Reused by every grey marker snapshot instead of rebuilding the IColor per order.
const UNSCHEDULED_MARKER_COLOR = getScaledColor(UNSCHEDULED_COLOR_HEX);

// Orders-map dot colors, matching the status chip in the orders table. Resolved fresh on each call
// (not cached) so they follow runtime theme changes, and read inside the function to stay import-safe
// for tests that mock `@routific/hive` with a partial `Theme.Colors`.
const getScheduledMarkerColor = (): IColor => getScaledColor(Theme.Colors.primary6);
const getUnscheduledOrderMarkerColor = (): IColor => getScaledColor(Theme.Colors.textSecondary);
const getDeliveredMarkerColor = (): IColor => getScaledColor(Theme.Colors.success6);
const getMissedMarkerColor = (): IColor => getScaledColor(Theme.Colors.warning6);

/* The TypeScript type of an instance of VisitDtoModel */
export interface VisitDtoModelType extends Instance<typeof VisitDtoModel.Type> {
}

/* A graphql query fragment builders for VisitDtoModel */
export { selectFromVisitDto, visitDtoModelPrimitives, VisitDtoModelSelector } from './VisitDtoModel.base';

export const NOTES2_MAPPING = 'notes2';
const SHOPIFY_ORDER_GID_PREFIX = 'gid://shopify/Order/';
const ZAPIER_CREATION_SOURCE = 'zapier';

export enum OrderStatus {
  MISSED = 'missed',
  DELIVERED = 'delivered',
  SCHEDULED = 'scheduled',
  UNSCHEDULED = 'unscheduled',
}

const getOrderStatusMarkerColor = (status: OrderStatus): IColor => {
  switch (status) {
    case OrderStatus.DELIVERED:
      return getDeliveredMarkerColor();
    case OrderStatus.MISSED:
      return getMissedMarkerColor();
    case OrderStatus.SCHEDULED:
      return getScheduledMarkerColor();
    case OrderStatus.UNSCHEDULED:
    default:
      return getUnscheduledOrderMarkerColor();
  }
};

/**
 * VisitDtoModel
 */
export const VisitDtoModel = VisitDtoModelBase
  .views(self => ({
    get tagNames(): string[] {
      return self.tags?.map((tag: { name: any }) => tag.name).filter((name: any): name is string => !!name) ?? [];
    },
    get isShopifyOrder(): boolean {
      return self.externalId?.startsWith(SHOPIFY_ORDER_GID_PREFIX) ?? false;
    },
    get isZapierOrder(): boolean {
      return self.creationSource?.toString().toLowerCase() === ZAPIER_CREATION_SOURCE;
    },
    get isHovered(): boolean {
      return getAppRoot(self).ui.hoveredVisit?.id === self.id || getAppRoot(self).mapView.hoveredMarkerVisitsList?.find(visit => visit.id === self.id) !== undefined;
    },
    get isSelected(): boolean {
      return getAppRoot(self).ui.selectedVisit?.id === self.id || !!getAppRoot(self).mapView.ordersWithinLasso?.find(visit => visit.id === self.id);
    },
    get isFilteredDate(): boolean {
      return getAppRoot(self).ui.selectedDate === self.deliveryDate || !self.deliveryDate;
    },
    get locationStatus() {
      return this.activeLocation?.locationStatusId;
    },
    get isGeocoding() {
      return this.locationStatus === LocationStatus.Geocoding;
    },
    get isInvalid() {
      return (this.activeLocation?.locationStatusId === LocationStatus.Invalid);
    },
    get isQuestionable() {
      return (this.activeLocation?.locationStatusId === LocationStatus.Validated && !this.activeLocation?.bestMatch);
    },
    get isLandline(): boolean {
      return Boolean(self.phone && isLandline(self.phone) && getAppRoot(self).customerNotification.cnSettings?.deliveryMethod === DeliveryMethod.SMS);
    },
    get activeLocation(): VisitLocationDtoModelType {
      return self.locations?.find((l) => l?.active) || self.locations?.[0];
    },
    get displayLocation(): string {
      return this.activeLocation?.displayAddress;
    },
    get hasAddressIssue(): boolean {
      return this.isQuestionable || this.isInvalid;
    },
    get sortedSuggestions(): VisitLocationDtoModelType[] {
      return self.locations && this.isQuestionable ? self.locations.slice().sort((a, b) => Number(a.id) - Number(b.id)) : [];
    },
    get hasLngLat(): boolean {
      return this.activeLocation?.latitude != null && this.activeLocation?.longitude != null;
    },
    get displayName() {
      return self.name ?? this.displayLocation;
    },
    get hasTimeWindow(): boolean {
      return !!self.timeWindows?.length;
    },
    // default to first (and only) time window until Multiple Time Windows feature implemented
    // https://app.asana.com/0/0/1200223679195313/1200224720614745/f
    get activeTimeWindow(): VisitTimeWindowDtoModelType | undefined {
      return this.hasTimeWindow ? self.timeWindows?.[0] : undefined;
    },
    get timeWindowString(): string | undefined {
      return this.activeTimeWindow?.formattedString;
    },
    get timeWindowObject(): IStopFormTimeWindow | undefined {
      return this.hasTimeWindow ? self.timeWindows?.[0].timeWindowObject : undefined;
    },
    // notes2 is a read-only legacy field from v2 which we've merged into custom notes on v2->v3 sync
    get notes2(): string | undefined {
      return self.customFields?.notes2?.trim();
    },
    get customNotesExcludingNotes2(): { [key: string]: any } | undefined {
      if (self.customFields && self.customFields.notes2) {
        const { notes2, ...rest } = self.customFields;

        return rest;
      }

      return self.customFields;
    },
    // `scheduledEvent` is an MSTGQLRef to a RouteSolutionDto. A CSV upload replaces the store's
    // route solutions, which can leave this ref dangling on already-scheduled visits — reading it
    // directly then throws "Failed to resolve reference" and crashes the orders page (ROUT-2850).
    // Resolve it defensively so a dangling ref reads as "not scheduled" until the store repopulates.
    get resolvedScheduledEvent(): RouteSolutionDtoModelType | undefined {
      try {
        return self.scheduledEvent ?? undefined;
      } catch {
        return undefined;
      }
    },
    // The RouteDto referenced by `scheduledEvent.route` is likewise an MSTGQLRef that can dangle
    // after a CSV upload; resolve it through the same guard.
    get resolvedScheduledRoute(): RouteDtoModelType | undefined {
      try {
        return this.resolvedScheduledEvent?.route ?? undefined;
      } catch {
        return undefined;
      }
    },
    // link visit back to assigned route (if present)
    get route(): RouteDtoModelType | undefined {
      // Traversing the list of routes is slow, we should skip it if not necessary
      if (!self.isScheduled) {
        return undefined;
      }

      return getAppRoot(self).sortedRoutes
        .find((route: RouteDtoModelType) => route.timeline?.find((e) => e.orders?.find((o) => o.uuid === self.uuid))
          && resolveIdentifier(RouteDtoModel, getAppRoot(self), route.id)) || this.resolvedScheduledRoute;
    },
    attempt(stopUuid?: string): OrderEventLogDtoModelType | undefined {
      if (stopUuid) {
        return self.attempts?.slice().reverse().find(log => log.eventUuid === stopUuid);
      }
      return self.attempts?.length ? self.attempts[self.attempts.length - 1] : undefined;
    },
    isMissed(stopUuid?: string): boolean {
      const attempt = this.attempt(stopUuid);
      return attempt?.status === OrderCompletionStatus.MISSED;
    },
    isDelivered(stopUuid?: string): boolean {
      const attempt = this.attempt(stopUuid);
      return attempt?.status === OrderCompletionStatus.DELIVERED;
    },
    get pendingTargetRoute(): RouteDtoModelType | undefined {
      return getAppRoot(self).sortedRoutes
        .find((route: RouteDtoModelType) => route.uuid === self.pendingTargetRouteUuid
          && resolveIdentifier(RouteDtoModel, getAppRoot(self), route.id));
    },
    get timeline(): RouteTimelineDtoModelType | undefined {
      const foundRoute = this.route;
      const selectedStopId = getAppRoot(self).ui.selectedRouteEvent?.id;

      const matchingEvents = foundRoute?.timeline?.filter((e) => e.orders?.find((o) => o.uuid === self.uuid));

      if (selectedStopId) {
        const selectedEvent = matchingEvents?.find(e => e.id === selectedStopId);
        if (selectedEvent) {
          return selectedEvent;
        }
      }

      return matchingEvents?.[0];
    },
    get isReassigning(): boolean {
      return !!self.isScheduling && !!this.pendingTargetRoute;
    },
    get isBeingModified(): boolean {
      return ((self.isScheduling || self.isUnscheduling || this.isReassigning) || false);
    },
    get customFieldsList(): TCustomField[] | undefined {
      return parseCustomFields(this.customNotesExcludingNotes2);
    },
    get pendingColor(): IColor | undefined {
      if (self.isUnscheduling) {
        return getScaledColor(UNSCHEDULED_COLOR_HEX);
      }

      return self.isScheduling ? this.pendingTargetRoute?.color : undefined;
    },
    get locked(): boolean {
      return !!self.lockedActionUuid;
    },
    get isDisabled(): boolean {
      return !self.isUnscheduling && !!(getAppRoot(self).ui.isOptimizing || this.locked) || this.isGeocoding;
    },
  }))
  .views(self => ({
    // The integration this order was imported from, if any. An order has at most one source, so
    // consumers take this single value rather than one boolean per integration.
    get orderSource(): OrderSource | undefined {
      if (self.isShopifyOrder) {
        return OrderSource.SHOPIFY;
      }
      if (self.isZapierOrder) {
        return OrderSource.ZAPIER;
      }
      return undefined;
    },
    // Shared marker snapshot for both maps. Returns a plain object rather than an MST node because
    // every consumer only reads it as data (straight into GeoJSON.parse / bounds), so we skip the
    // per-order node creation. Callers supply the route (for id/uuid keying) and the fill color,
    // which is the only thing that differs between the two maps.
    buildMarkerSnapshot(route?: RouteDtoModelType, color: IColor = UNSCHEDULED_MARKER_COLOR): MapMarkerSnapshot {
      return {
        id: self.id,
        type: 'unscheduled',
        routeId: route?.id ?? self.pendingTargetRoute?.id,
        routeUuid: route?.uuid ?? self.pendingTargetRoute?.uuid,
        latitude: self.activeLocation?.latitude || 0,
        longitude: self.activeLocation?.longitude || 0,
        color,
        pendingColor: self.pendingColor,
        isPending: self.isBeingModified,
      };
    },
    // Scheduling-map marker: route-agnostic (grey), keyed only to the pending target route.
    get marker(): MapMarkerSnapshot {
      return this.buildMarkerSnapshot();
    },
    get isReattemptStop(): boolean {
      if (self.timeline?.reattemptEventUuid) {
        return true;
      }

      const stopUuid = self.timeline?.eventUuid;
      if (!stopUuid || !self.attempts?.length) {
        return false;
      }

      const requestedReattempt = self.attempts.some(attempt => attempt.reattemptRequestDate);
      if (!requestedReattempt) {
        return false;
      }

      return !self.attempts.some(attempt => attempt.eventUuid === stopUuid);
    },
    get isArrivedStop(): boolean {
      if (!self.isScheduled) {
        return false;
      }

      const timeline = self.timeline;
      if (timeline) {
        return !!timeline.isArrived;
      }

      // OrderTimelineQuery loads events into the store without populating the route's timeline.
      // When order details are opened before the route is loaded, use those events directly,
      // restricted to the order's current route solution so cached arrivals don't affect it.
      const solutionId = self.resolvedScheduledEvent?.id;
      if (!self.uuid || !solutionId) {
        return false;
      }

      const { model, ui } = getAppRoot(self);
      const events = (model.orderTimelineEventsByOrderAndSolution.get(self.uuid)?.get(solutionId) ?? [])
        .slice()
        .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
      const event = events.find(event => event.id === ui.selectedRouteEvent?.id) ?? events[0];

      return !!event?.isArrived;
    },
    get durationMinutes(): number | null | undefined {
      if (typeof self.duration === 'number') {
        return parseFloat((self.duration / 60).toFixed(2));
      }
      return self.duration;
    },
    // A stop the driver is only on the way to stays modifiable; planners routinely agree those
    // changes with the driver directly. Arrival is where it closes.
    get canBeModified(): boolean {
      return !(self.isDisabled || self.isCompleted || this.isArrivedStop || self.isGeocoding);
    },
    getAssociatedStopUuids(): string[] {
      if (self.route && self.route.timeline.length > 0) {
        const matchingEvents: RouteTimelineDtoModelType[] = self.route.timeline?.filter((e) =>
          e.orders?.find((o) => o.uuid === self.uuid),
        );

        return matchingEvents
          .map(event => event.eventUuid)
          .filter((uuid): uuid is string => uuid !== undefined);

      } else {
        return [...new Set(self.attempts?.map(eventLog => eventLog.eventUuid))];
      }
    },
  }))
  .views(self => ({
    status(stopUuid?: string): OrderStatus {
      if (self.isMissed(stopUuid)) {
        return OrderStatus.MISSED;
      }
      if (self.isDelivered(stopUuid)) {
        return OrderStatus.DELIVERED;
      }
      // When a specific stop was queried but no matching attempt was found,
      // fall back to the overall completion status. This handles cases where
      // the route solution changed after delivery, causing the timeline's
      // eventUuid to no longer match the attempt's eventUuid.
      if (stopUuid && self.isCompleted) {
        if (self.isMissed()) {
          return OrderStatus.MISSED;
        }
        if (self.isDelivered()) {
          return OrderStatus.DELIVERED;
        }
      }
      if (self.isScheduled) {
        return OrderStatus.SCHEDULED;
      }
      return OrderStatus.UNSCHEDULED;
    },
    // Orders-map marker: colored by order status to match the status chip in the orders table. Route
    // (for id/uuid keying) comes from `scheduledEvent.route` — the same source the table's Route column
    // uses — not the `route` getter, which scans `sortedRoutes` (slow, and unloaded on the orders page).
    get orderMapMarker(): MapMarkerSnapshot {
      const route = this.resolvedScheduledRoute;
      return self.buildMarkerSnapshot(route, getOrderStatusMarkerColor(this.status()));
    },
  }))
  .actions((self) => ({
    resolveAddress(onSingleSelection?: (id: string) => void) {
      const { ui } = getAppRoot(self);
      if (!self.isSelected) {
        onSingleSelection?.(self.id);
      }

      ui.setOrderForm({ visible: true, visit: self as VisitDtoModelType, saving: false });
    },
    setIsUnscheduling(isUnscheduling: boolean) {
      self.isUnscheduling = isUnscheduling;
    },
  }));
